'use client';

import { Award, Download } from 'lucide-react';

import type { CertidaoEncerramento } from '../types/encerramento.types';
import { formatarData, formatarTamanho } from '../utils/formatarEncerramento';
import { classesDoTom } from '../utils/tonsEncerramento';

interface GrupoEncerramentoCertificadosProps {
  readonly certidoes: readonly CertidaoEncerramento[];
  readonly carregando?: boolean;
  readonly onBaixar?: (id: string) => void;
}

export function GrupoEncerramentoCertificados({
  certidoes,
  carregando = false,
  onBaixar,
}: GrupoEncerramentoCertificadosProps) {
  if (carregando) {
    return (
      <section aria-busy="true">
        <Titulo total={null} />
        <div className="mt-3 h-14 animate-pulse rounded-[16px] bg-neutral-100" />
      </section>
    );
  }

  if (certidoes.length === 0) {
    return (
      <section>
        <Titulo total={0} />
        <p className="mt-3 rounded-[16px] border border-dashed border-neutral-200 p-3 text-xs text-neutral-500">
          A certidão de trânsito em julgado aparece aqui quando o processo for encerrado.
        </p>
      </section>
    );
  }

  return (
    <section>
      <Titulo total={certidoes.length} />
      <ul className="mt-3">
        {certidoes.map((certidao) => (
          <ItemCertidao certidao={certidao} key={certidao.id} onBaixar={onBaixar} />
        ))}
      </ul>
    </section>
  );
}

function Titulo({ total }: { readonly total: number | null }) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-medium text-neutral-900">
      Encerramento
      {total === null ? null : (
        <span className="rounded-md bg-neutral-50 px-2 py-0.5 text-xs text-neutral-600">
          {total}
        </span>
      )}
    </h3>
  );
}

function ItemCertidao({
  certidao,
  onBaixar,
}: {
  readonly certidao: CertidaoEncerramento;
  readonly onBaixar?: (id: string) => void;
}) {
  const tomErro = classesDoTom('erro');
  const tamanho = formatarTamanho(certidao.tamanhoBytes);
  const podeBaixar = certidao.status === 'GERADO';
  const cancelada = certidao.status === 'CANCELADO';

  return (
    <li className="flex items-center gap-2.5 border-t border-neutral-200 py-2.5">
      <Award aria-hidden="true" className="text-brand-600 h-4.5 w-4.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p
          className={
            cancelada ? 'text-sm text-neutral-500 line-through' : 'text-sm text-neutral-900'
          }
        >
          {certidao.nome}
        </p>
        <p className="text-xs text-neutral-600">
          Emitida em {formatarData(certidao.emitidaEm)}
          {tamanho ? ` · ${tamanho}` : ''}
          {certidao.status === 'EM_GERACAO' ? ' · em geração…' : ''}
          {certidao.status === 'INDISPONIVEL' ? ' · arquivo indisponível' : ''}
        </p>
        {cancelada ? (
          <p className={`text-xs ${tomErro.texto}`}>
            Documento cancelado — mantido para rastreabilidade
          </p>
        ) : null}
      </div>
      <button
        aria-label={`Baixar ${certidao.nome}`}
        className="shrink-0 rounded-[8px] p-1.5 text-neutral-600 disabled:opacity-40"
        disabled={!podeBaixar || !onBaixar}
        onClick={() => onBaixar?.(certidao.id)}
        type="button"
      >
        <Download aria-hidden="true" className="h-4 w-4" />
      </button>
    </li>
  );
}
